import {
  AppCard,
  Icon,
  PageContainer,
  RouteIconButtonLink,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import { TabContext } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  prefetchListQuotationBatchesQuery,
  useListQuotationBatchesSuspense,
} from "../../../../__generated__/orval/endpoints/quotation-batches/quotation-batches";
import type { EntitiesQuotationBatchListResponse } from "../../../../__generated__/orval/models/entitiesQuotationBatchListResponse.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { QuotationBatchesFilter } from "./-internal_components/QuotationBatchesFilter";

const searchSchema = z.object({
  id: z.optional(z.string()).catch(undefined),
  name: z.optional(z.string()).catch(undefined),
  order: z
    .optional(
      z.object({
        id: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        name: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        responsible: z.optional(z.enum(["asc", "desc"])).catch(undefined),
      }),
    )
    .default({ id: "desc" })
    .catch({ id: "desc" }),
  page: z.optional(z.coerce.number()).catch(undefined),
  responsibleId: z.optional(z.array(z.coerce.number())).catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  tab: z.enum(["open", "closed"]).default("open").catch("open"),
});

type TabType = z.infer<typeof searchSchema.shape.tab>;

type OrderSearchSchema = z.infer<typeof searchSchema.shape.order>;

const QuotationBatchTableRow: React.FC<{
  quotationBatch: EntitiesQuotationBatchListResponse["results"][number];
}> = ({ quotationBatch }) => {
  return (
    <TableRow>
      <TableCell>{quotationBatch.id}</TableCell>
      <TableCell>{quotationBatch.name}</TableCell>
      <TableCell>{quotationBatch.responsible?.full_name_with_login}</TableCell>
      <TableCell>
        <RouteIconButtonLink
          params={{
            quotationBatchId: quotationBatch.id.toString(),
          }}
          to="/vbuyer/quotation_batches/$quotationBatchId"
        >
          <Icon icon={mdiEye} />
        </RouteIconButtonLink>
      </TableCell>
    </TableRow>
  );
};

const QuotationBatchTable: React.FC = () => {
  const navigate = Route.useNavigate();
  const { page = 1, rowsPerPage = 25, order = {} } = Route.useSearch();
  const loaderDeps = Route.useLoaderDeps();

  const { t } = useTranslation();

  const { data: quotationBatchesResponse } =
    useListQuotationBatchesSuspense(loaderDeps);
  // useSuspenseQuery(
  //   queryOptions(useListQuotationBatchesQueryOptions(loaderDeps)),
  // );

  const handleChangePage = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      newPage: number,
    ) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: newPage + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (column: keyof OrderSearchSchema) => {
      const isAsc = order[column] === "asc";
      const isDesc = order[column] === "desc";

      if (isDesc) {
        // If currently descending, remove the sort
        navigate({
          search: (prev) => {
            const newOrder = { ...prev.order };
            delete newOrder[column];
            return {
              ...prev,
              order: newOrder,
              page: 1,
            };
          },
        });
        return;
      }

      // If currently ascending or not sorted, set to ascending or descending
      navigate({
        search: (prev) => ({
          ...prev,
          order: {
            ...prev.order,
            [column]: isAsc ? "desc" : "asc",
          },
          page: 1,
        }),
      });
    },
    [navigate, order],
  );

  useEffect(() => {
    if (!quotationBatchesResponse) return;

    const {
      pagination: {
        current_page: currentPage,
        items_per_page: currentItemsPerPage,
      },
    } = quotationBatchesResponse;

    if (currentPage !== page || currentItemsPerPage !== rowsPerPage) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          page: currentPage,
          rowsPerPage: currentItemsPerPage,
        }),
      });
    }
  }, [quotationBatchesResponse, page, rowsPerPage, navigate]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={!!order.id}
                direction={order.id === "asc" ? "asc" : "desc"}
                onClick={() => handleSort("id")}
              >
                {t("common.id")}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={!!order.name}
                direction={order.name === "asc" ? "asc" : "desc"}
                onClick={() => handleSort("name")}
              >
                {t("common.name")}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={!!order.responsible}
                direction={order.responsible === "asc" ? "asc" : "desc"}
                onClick={() => handleSort("responsible")}
              >
                {t("common.responsible")}
              </TableSortLabel>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {quotationBatchesResponse.results.map((quotationBatch) => (
            <QuotationBatchTableRow
              key={quotationBatch.id}
              quotationBatch={quotationBatch}
            />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={quotationBatchesResponse.total_items}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const RouteComponent: React.FC = () => {
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();

  const { t } = useTranslation();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton={false}
      title={t("common.quotationBatches")}
    >
      <AppCard>
        <QuotationBatchesFilter />

        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleTabChange} value={tab}>
              <RouteTabLink
                from="/vbuyer/quotation_batches/"
                label={t("common.open")}
                search={(prev) => ({ ...prev, page: 1, tab: "open" })}
                to="/vbuyer/quotation_batches"
                value="open"
              />
              <RouteTabLink
                from="/vbuyer/quotation_batches/"
                label={t("common.closed")}
                search={(prev) => ({ ...prev, page: 1, tab: "closed" })}
                to="/vbuyer/quotation_batches"
                value="closed"
              />
            </Tabs>
          </Box>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} sx={{ margin: 2 }} />
              </Box>
            }
          >
            <QuotationBatchTable />
          </Suspense>
        </TabContext>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotation_batches/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: RouteComponent,
  loaderDeps: ({ search }) => ({
    closed: search.tab === "closed",
    id: search.id ? [parseInt(search.id, 10)] : undefined,
    items_per_page: search.rowsPerPage,
    name: search.name,
    "order[id]": search.order.id,
    "order[name]": search.order.name,
    "order[responsible.full_name_with_login]": search.order.responsible,
    page: search.page,
    responsible_id: search.responsibleId?.join(","),
  }),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("View Quotation Batches")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    prefetchListQuotationBatchesQuery(context.queryClient, deps);

    return null;
  },
});
